import { BadgeType } from '@wix/members-badge-lib';
import {
  IMembersAreaWidgetPluginService,
  IRouteDataService,
} from '@wix/members-area-widget-plugin-lib/viewer';

import {
  Handler,
  InjectedGlobalSettings,
  InjectedSite,
  Nullable,
  PublicMember,
} from '../types';
import { FlowAPI, WarmupDataKey } from '../types/controller';

import GlobalSettingsBuilder from './global-settings-builder';
import MembersService from './members-service';
import BadgesService from './badges-service';
import SettingsService from './settings-service';
import { getIsPublicMemberCandidateNoOne } from './members-privacy';

interface CreateInitialDataFetchServiceOptions {
  flowAPI: FlowAPI;
  getInstance: Handler<string>;
  services: DataServices;
}

interface DataServices {
  membersService: MembersService;
  badgesService: BadgesService;
  settingsService: SettingsService;
  routerDataService: IRouteDataService;
  widgetPluginService: IMembersAreaWidgetPluginService;
}

interface InitialData {
  currentMember: Nullable<PublicMember>;
  viewedMember: Nullable<PublicMember>;
  installedApps: InjectedSite['installedApps'];
  isSocialChat: InjectedSite['isSocialChat'];
  rolesMap: Nullable<{ [key: string]: { roleName: string } }>;
  badgeList: BadgeType[];
  globalSettings: InjectedGlobalSettings;
  isPublicMemberCandidateNoOne: boolean;
  currentTabId: string;
  isWidgetPlugin: boolean;
}

export default class InitialDataFetchService {
  constructor(
    private readonly flowAPI: FlowAPI,
    private readonly getInstance: Handler<string>,
    private readonly dataServices: DataServices,
  ) {}

  async fetchInitialData(): Promise<InitialData> {
    const { wixCodeApi } = this.flowAPI.controllerConfig;
    const routeData = await this.dataServices.routerDataService.getRouteData();
    const currentMemberId = routeData?.currentMemberIdentifier?.id ?? null;
    const viewedMemberId = routeData?.viewedMemberIdentifier?.id ?? null;

    const cacheKey = this.getCacheKey(currentMemberId, viewedMemberId);
    const cachedInitialData = this.getInitialDataFromCache(cacheKey);
    if (cachedInitialData) {
      return cachedInitialData;
    }

    let initialData: InitialData;
    if (this.flowAPI.environment.isSSR) {
      initialData = await this.getInitialDataFromServices(
        currentMemberId,
        viewedMemberId,
      );
      wixCodeApi.window.warmupData.set(WarmupDataKey.InitialData, initialData);
    } else {
      const warmupData = wixCodeApi.window.warmupData.get(
        WarmupDataKey.InitialData,
      );
      initialData =
        warmupData ??
        (await this.getInitialDataFromServices(
          currentMemberId,
          viewedMemberId,
        ));
    }

    this.addInitialDataToCache(cacheKey, initialData);

    return initialData;
  }

  clearCache(
    currentMemberId: Nullable<string>,
    viewedMemberId: Nullable<string>,
  ) {
    try {
      const cacheKey = this.getCacheKey(currentMemberId, viewedMemberId);
      return this.getStorage()?.removeItem(cacheKey);
    } catch {}
  }

  private async getInitialDataFromServices(
    currentMemberId: Nullable<string>,
    viewedMemberId: Nullable<string>,
  ): Promise<InitialData> {
    const [
      currentMember,
      viewedMember,
      installedApps,
      isSocialChat,
      rolesMap,
      badgeList,
      globalSettings,
      isPublicMemberCandidateNoOne,
      currentTabId,
      isWidgetPlugin,
    ] = await Promise.all([
      ...this.getMembers(currentMemberId, viewedMemberId),
      this.getInstalledApps(),
      this.getIsSocialChat(),
      this.getRolesMap(),
      this.getBadges(),
      this.getGlobalSettings(),
      getIsPublicMemberCandidateNoOne(this.flowAPI.httpClient),
      this.getCurrentTabId(),
      this.getIsWidgetPlugin(),
    ]);

    return {
      currentMember,
      viewedMember,
      installedApps,
      isSocialChat,
      rolesMap,
      badgeList,
      globalSettings,
      isPublicMemberCandidateNoOne,
      currentTabId,
      isWidgetPlugin,
    };
  }

  private getCurrentTabId() {
    return this.dataServices.widgetPluginService.getCurrentTabId();
  }

  private getIsWidgetPlugin() {
    return this.dataServices.widgetPluginService.getIsWidgetPlugin();
  }

  private getMembers(
    currentMemberId: Nullable<string>,
    viewedMemberId: Nullable<string>,
  ): [Promise<Nullable<PublicMember>>, Promise<Nullable<PublicMember>>] {
    const { membersService } = this.dataServices;
    const currentMemberPromise = currentMemberId
      ? membersService.getMember(currentMemberId)
      : Promise.resolve(null);

    if (currentMemberId === viewedMemberId) {
      return [currentMemberPromise, currentMemberPromise];
    }

    const viewedMemberPromise = viewedMemberId
      ? membersService.getMember(viewedMemberId)
      : Promise.resolve(null);

    return [currentMemberPromise, viewedMemberPromise];
  }

  private getInstalledApps() {
    try {
      return this.dataServices.membersService.getInstalledApps();
    } catch {
      return Promise.resolve({});
    }
  }

  private getIsSocialChat() {
    try {
      return this.dataServices.membersService.getIsSocialChat();
    } catch {
      return Promise.resolve(false);
    }
  }

  private getRolesMap() {
    try {
      return this.dataServices.membersService.getRolesMap();
    } catch {
      return Promise.resolve({});
    }
  }

  private getBadges() {
    try {
      return this.dataServices.badgesService.getBadgeList();
    } catch {
      return Promise.resolve([]);
    }
  }

  private getGlobalSettings() {
    try {
      const { viewMode } = this.flowAPI.controllerConfig.wixCodeApi.window;
      return this.dataServices.settingsService.getGlobalSettings(viewMode);
    } catch {
      const globalSettings = new GlobalSettingsBuilder().build();
      return Promise.resolve(globalSettings);
    }
  }

  private getInitialDataFromCache(cacheKey: string) {
    const storageResponse = this.getStorage()?.getItem(cacheKey);

    if (!storageResponse) {
      return null;
    }

    try {
      return JSON.parse(storageResponse);
    } catch {
      return null;
    }
  }

  private addInitialDataToCache(cacheKey: string, initialData: InitialData) {
    try {
      return this.getStorage()?.setItem(cacheKey, JSON.stringify(initialData));
    } catch {}
  }

  private getCacheKey(
    currentMemberId: Nullable<string>,
    viewedMemberId: Nullable<string>,
  ) {
    const [instance] = this.getInstance().split('.');
    const { appDefinitionId } = this.flowAPI.controllerConfig.appParams;
    const memberIds = [currentMemberId, viewedMemberId].filter(Boolean);

    return memberIds.length
      ? `${appDefinitionId}-${memberIds.join('-')}-${instance}`
      : `${appDefinitionId}-${instance}`;
  }

  private getStorage() {
    const { platformAPIs } = this.flowAPI.controllerConfig;
    return platformAPIs?.storage?.memory;
  }
}

export const createInitialDataFetchService = ({
  flowAPI,
  getInstance,
  services,
}: CreateInitialDataFetchServiceOptions) => {
  return new InitialDataFetchService(flowAPI, getInstance, services);
};
